import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { CreateDataAnalysisQueryGQL, CreateDataAnalysisQueryInput, DeleteDataAnalysisQueryGQL, GetControlSystemListGQL, GetDataAnalysisParkTurbineSignalGQL, GetDataAnalysisParkTurbineSignalQuery, GetDataAnalysisQueryDetailsGQL, GetMetmastListGQL, GetQueriesListForReportGQL, GetQueriesListGQL, UpdateDataAnalysisQueryGQL } from '../graphql';
import { DataStudioModel } from 'app/models/data-studio/data-studio.model';

import { DataStudioParksList, IDataStudioPark } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { DimensionService } from 'app/analyze/service/dimension-service/dimension.service';
import { CurrencyService } from '../../currency-service/currency.service';

@Injectable({
  providedIn: 'root'
})
export class DataStudioService {

  constructor(
    private dataAnalysisParkTurbineSignal: GetDataAnalysisParkTurbineSignalGQL,
    private createDataAnalysisQueryGQL: CreateDataAnalysisQueryGQL,
    private getQueriesListGQL: GetQueriesListGQL,
    private getQueriesListForReportGQL: GetQueriesListForReportGQL,
    private getQueryDetailsGQL: GetDataAnalysisQueryDetailsGQL,
    private updateDataAnalysisQueryGQL: UpdateDataAnalysisQueryGQL,
    private deleteDataAnalysisQueryGQL: DeleteDataAnalysisQueryGQL,
    private dimensionService: DimensionService,
    private getControlSystemListGQL: GetControlSystemListGQL,
    private getMetmastListGQL: GetMetmastListGQL,
    private currencyService: CurrencyService
  ) { }

  public getDataAnalysisParkTurbineSignal(streamType): Observable<IDataStudioPark[]> {
    return this.dataAnalysisParkTurbineSignal.fetch({
        variables: { streamType },
        fetchPolicy: 'cache-first'
    })
    .pipe(map((result: any)=> {
      const currency = this.currencyService.getCurrencySymbol();
      const parks: DataStudioParksList[] = result.data.parks;
      return DataStudioModel.mapDataStudioParks(parks, currency);
    }));
  }

  public getQueries(pageNumber = 0, pageSize = 10, titleFilter = '') {
    const controlSystemsData$ = this.getControlSystemListGQL.fetch();
    const metmastData$ = this.getMetmastListGQL.fetch();
    const queriesList$ = this.getQueriesListGQL.fetch({
      variables: {
        pageNumber, pageSize, isSharedQuery: false, titleFilter
      },  
      fetchPolicy: 'network-only' 
    });
    return combineLatest([controlSystemsData$, queriesList$, metmastData$])
      .pipe(map(([controlSystemsData, data, metmastData]) => {
        const controlSystemsList = controlSystemsData.data.parks.map(park => park.controlSystem).filter(cs => cs.length > 0).flat();
        const metmastSensorList = metmastData.data.parks.flatMap(park => park.metmasts.flatMap(metmast => metmast.sensorsList));
        return DataStudioModel.queryListMapper(data, this.dimensionService, controlSystemsList, metmastSensorList)
      }));
  }

  public getQueriesForReport() {
    return this.getQueriesListForReportGQL.fetch().pipe(map(data => DataStudioModel.queryListMapperForReport(data)));
  }

  public getSharedQueries(pageNumber = 0, pageSize = 10, titleFilter = '') {
    const controlSystemsData$ = this.getControlSystemListGQL.fetch();
    const metmastData$ = this.getMetmastListGQL.fetch();
    const queriesList$ = this.getQueriesListGQL.fetch({
      variables: {
        pageNumber, pageSize, isSharedQuery: true, titleFilter
      },
      fetchPolicy: 'network-only'
    });
    return combineLatest([controlSystemsData$, queriesList$, metmastData$])
      .pipe(map(([controlSystemsData, data, metmastData]) => {
        const controlSystemsList = controlSystemsData.data.parks.map(park => park.controlSystem).filter(cs => !!cs.length).flat();
        const metmastSensorList = metmastData.data.parks.flatMap(park => park.metmasts.flatMap(metmast => metmast.sensorsList));
        return DataStudioModel.queryListMapper(data, this.dimensionService, controlSystemsList, metmastSensorList)
      }));
  }

  public getQueryDetails(id) {
    const controlSystemsData$ = this.getControlSystemListGQL.fetch();
    const metmastData$ = this.getMetmastListGQL.fetch();
    const queryDetails$ = this.getQueryDetailsGQL.fetch({
        variables: { id},  
        fetchPolicy: 'network-only' 
    });
    return combineLatest([controlSystemsData$, queryDetails$, metmastData$])
      .pipe(map(([controlSystemsData, data, metmastData]) => {
        const controlSystemsList = controlSystemsData.data.parks.map(park => park.controlSystem).filter(cs => !!cs.length).flat();
        const metmastSensorList = metmastData.data.parks.flatMap(park => park.metmasts.flatMap(metmast => metmast.sensorsList));
        return DataStudioModel.queryDetailsMapper(data.data.dataAnalysisQuery,this.dimensionService, controlSystemsList, metmastSensorList);
      }));
  }

  public getQuerySeries(id) {
    return this.getQueryDetailsGQL.fetch({
      variables: { id },
      fetchPolicy: 'network-only'
    }).pipe(map(data => DataStudioModel.createQuerySeriesMapper(data.data.dataAnalysisQuery.series)));
  }

  public saveNewQuery(id, value) {
    localStorage.setItem(id, value);
  }

  // public updateQuery(id, value) {
  //   localStorage.setItem(id, value);
  // }

  // public deleteQuery(id) {
  //   localStorage.removeItem(id);
  // }

  public getQuery(id) { // get mock data for data analysis
    // return of(JSON.parse(localStorage.getItem(id)));
    // return combineLatest([JSON.parse(localStorage.getItem(id)), getAnalysisData()])
    //         .pipe(map(([data, defaultData]) => data || defaultData))
  }

  public createQuery(query: CreateDataAnalysisQueryInput) {
    return this.createDataAnalysisQueryGQL.mutate({variables: {query}}).pipe(map(data => {
      return data;
    }))
  }

  public updateQuery(query) {
    return this.updateDataAnalysisQueryGQL.mutate({variables: {query}}).pipe(map(data => data.data?.updateDataAnalysisQuery));
  }

  public deleteQuery(id) {
    return this.deleteDataAnalysisQueryGQL.mutate({variables: {id}}).pipe(map(data => data.data?.deleteDataAnalysisQuery));
  }
}
